import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';
import { supabase } from '../lib/supabase';

/**
 * Keep the user's language preference in sync between i18next and their
 * `profiles.language` column. The DB row is the source of truth for signed-in
 * users; localStorage remains the fallback for anonymous sessions.
 *
 * On sign-in: pull `profiles.language` and apply it (overrides any locally
 * cached preference from a previous anonymous session).
 *
 * On language change while signed in: persist to DB so the choice is honored
 * cross-device and by backend personalization (emails, SMS, push).
 */
export function useLanguageProfileSync() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const justHydratedRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from('profiles')
      .select('language')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled || error || !data?.language) return;
        const stored = data.language.slice(0, 2);
        if (!SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) return;
        if (stored !== (i18n.resolvedLanguage || i18n.language).slice(0, 2)) {
          justHydratedRef.current = true;
          i18n.changeLanguage(stored).finally(() => {
            justHydratedRef.current = false;
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, i18n]);

  useEffect(() => {
    if (!user) return;
    const handler = (lng: string) => {
      if (justHydratedRef.current) return;
      const short = lng.slice(0, 2);
      if (!SUPPORTED_LANGUAGES.includes(short as SupportedLanguage)) return;
      supabase
        .from('profiles')
        .update({ language: short })
        .eq('id', user.id)
        .then(({ error }) => {
          if (error) console.error('Failed to persist language preference:', error);
        });
    };
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [user?.id, i18n]);
}
